import React, { useState } from 'react'
import { leaV3API, AffaireHistory, RouteApplication, ThreadEvent } from '../api/leaV3.api'

export const HistoryView: React.FC = () => {
  const [affaireId, setAffaireId] = useState('')
  const [data, setData] = useState<AffaireHistory | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    const id = parseInt(affaireId, 10)
    if (!id) return
    setLoading(true); setError(null); setData(null)
    try {
      const r = await leaV3API.getAffaireHistory(id)
      setData(r.data)
    } catch (e: any) {
      setError(e?.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-2 items-center">
        <label className="text-sm text-gray-600">Affaire ID</label>
        <input
          className="border rounded px-2 py-1 text-sm w-32"
          placeholder="ex: 72206"
          value={affaireId}
          onChange={(e) => setAffaireId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-1 bg-[#ef5343] text-white rounded text-sm disabled:opacity-50"
        >
          {loading ? '…' : 'Charger'}
        </button>
        <div className="text-xs text-gray-500 ml-4">
          Essaie : <button className="underline" onClick={() => { setAffaireId('72206'); }}>72206</button>{' '}
          ou <button className="underline" onClick={() => { setAffaireId('72256'); }}>72256</button>
        </div>
      </div>

      {error && <div className="p-4 text-red-600">Erreur : {error}</div>}

      {data && (
        <>
          <AffaireHeader data={data} />
          <LadderSummary data={data} />
          <Timeline data={data} />
        </>
      )}
    </div>
  )
}

const AffaireHeader: React.FC<{ data: AffaireHistory }> = ({ data }) => {
  const a = data.affaire
  return (
    <header className="bg-white border border-gray-200 rounded-lg p-4 flex gap-6">
      <div>
        <div className="text-xs text-gray-500">Affaire</div>
        <div className="font-mono font-bold text-lg">#{a.affaire_id}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Statut</div>
        <div className="font-semibold">{a.status}</div>
      </div>
      <div>
        <div className="text-xs text-gray-500">Statut changé le</div>
        <div className="text-sm">{new Date(a.last_status_change_at).toLocaleString('fr-BE')}</div>
      </div>
      {data.current_timeout && (
        <div className={`ml-auto p-2 rounded ${data.current_timeout.is_expired ? 'bg-[#fff1ef]' : 'bg-blue-50'}`}>
          <div className="text-xs text-gray-500">Prochain timeout</div>
          <div className="text-sm font-mono">
            {data.current_timeout.ladder_slot || data.current_timeout.category}
          </div>
          <div className="text-xs">
            {data.current_timeout.is_expired ? '⚠ expiré : ' : '⏰ '}
            {new Date(data.current_timeout.expires_at).toLocaleString('fr-BE')}
          </div>
        </div>
      )}
    </header>
  )
}

const LadderSummary: React.FC<{ data: AffaireHistory }> = ({ data }) => {
  const cats = Object.keys(data.ladder_history)
  if (cats.length === 0) return null

  const chipFor = (outcome: string) => {
    switch (outcome) {
      case 'auto_executed':
        return { cls: 'bg-green-100 text-green-800 border-green-300', icon: '✅', label: 'exécuté' }
      case 'plan_created':
        return { cls: 'bg-amber-100 text-amber-900 border-amber-300', icon: '🕐', label: 'plan en attente FCH' }
      case 'pending_validation':
        return { cls: 'bg-amber-100 text-amber-900 border-amber-300', icon: '🕐', label: 'plan en attente FCH' }
      case 'no_action_already_handled':
        return { cls: 'bg-gray-100 text-gray-700 border-gray-300', icon: '⏸', label: 'skippé (already handled)' }
      case 'no_route':
        return { cls: 'bg-red-50 text-red-700 border-red-200', icon: '⚠', label: 'no route' }
      case 'failed':
        return { cls: 'bg-red-100 text-red-800 border-red-300', icon: '✗', label: 'failed' }
      default:
        return { cls: 'bg-gray-100 text-gray-700 border-gray-300', icon: '·', label: outcome }
    }
  }

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold mb-3 text-gray-700">Progression ladder</h3>
      {cats.map((cat) => {
        // Dedupe : group fired entries by ladder_slot, keep only the latest per slot
        const bySlot = new Map<string, typeof data.ladder_history[string][number]>()
        for (const s of data.ladder_history[cat]) {
          const existing = bySlot.get(s.ladder_slot)
          if (!existing || s.fired_at > existing.fired_at) bySlot.set(s.ladder_slot, s)
        }
        const uniqueFires = Array.from(bySlot.values()).sort((a, b) => a.fired_at.localeCompare(b.fired_at))

        // Determine if "next" chip should show : only if different slot than the most-recent fire
        // (otherwise it's a duplicate and means the ladder hasn't advanced yet)
        const lastFiredSlot = uniqueFires.length ? uniqueFires[uniqueFires.length - 1].ladder_slot : null
        const showNext =
          data.current_timeout?.category === cat &&
          data.current_timeout.ladder_slot &&
          data.current_timeout.ladder_slot !== lastFiredSlot

        return (
          <div key={cat} className="mb-3 last:mb-0">
            <div className="text-xs text-gray-500 mb-1">{cat}</div>
            <div className="flex gap-2 flex-wrap">
              {uniqueFires.map((s, i) => {
                const c = chipFor(s.outcome)
                return (
                  <span
                    key={i}
                    className={`px-3 py-1 rounded-full text-xs border ${c.cls}`}
                    title={`${c.label} · ${s.outcome} via ${s.route_id}`}
                  >
                    {c.icon} {s.ladder_slot} · {new Date(s.fired_at).toLocaleDateString('fr-BE')}
                  </span>
                )
              })}
              {showNext && (
                <span className="px-3 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800 border border-yellow-300">
                  ⏰ next : {data.current_timeout!.ladder_slot} · {new Date(data.current_timeout!.expires_at).toLocaleDateString('fr-BE')}
                </span>
              )}
              {!showNext && data.current_timeout?.category === cat && (
                <span className="text-xs text-gray-400 self-center italic">
                  ladder reste sur ce slot tant que le plan n'est pas validé/exécuté
                </span>
              )}
            </div>
          </div>
        )
      })}
    </section>
  )
}

const Timeline: React.FC<{ data: AffaireHistory }> = ({ data }) => {
  // Merge route_applications + thread_events into single chronological list
  type Item =
    | { kind: 'route_app'; time: string; data: RouteApplication }
    | { kind: 'thread'; time: string; data: ThreadEvent }
  const items: Item[] = [
    ...data.route_applications.map((r) => ({ kind: 'route_app' as const, time: r.fired_at, data: r })),
    ...data.thread_events.map((t) => ({ kind: 'thread' as const, time: t.created_at, data: t }))
  ]
  items.sort((a, b) => b.time.localeCompare(a.time))

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-4">
      <h3 className="font-semibold mb-3 text-gray-700">Timeline ({items.length} events)</h3>
      <div className="space-y-2">
        {items.map((it, i) =>
          it.kind === 'route_app' ? <RouteAppRow key={i} r={it.data} /> : <ThreadRow key={i} t={it.data} />
        )}
      </div>
    </section>
  )
}

const RouteAppRow: React.FC<{ r: RouteApplication }> = ({ r }) => {
  const color =
    r.outcome === 'plan_created' || r.outcome === 'auto_executed'
      ? 'border-l-green-500 bg-green-50'
      : r.outcome === 'no_route'
      ? 'border-l-gray-400 bg-gray-50'
      : r.outcome === 'no_action_already_handled'
      ? 'border-l-blue-400 bg-blue-50'
      : 'border-l-yellow-500 bg-yellow-50'
  return (
    <div className={`border-l-4 ${color} px-3 py-2 rounded`}>
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold uppercase text-gray-700">⚙ Scheduler</span>
        <span className="font-mono text-xs">{r.route_id || '—'}</span>
        {r.ladder_slot && <span className="font-mono text-xs text-gray-600">[{r.ladder_slot}]</span>}
        <span className="text-xs text-gray-500 ml-auto">{new Date(r.fired_at).toLocaleString('fr-BE')}</span>
      </div>
      <div className="text-sm mt-0.5">
        <span className="font-semibold">{r.outcome}</span>
        {r.reason && <span className="text-gray-600"> — {r.reason}</span>}
      </div>
    </div>
  )
}

const ThreadRow: React.FC<{ t: ThreadEvent }> = ({ t }) => {
  const colors: Record<string, string> = {
    email: 'border-l-purple-400 bg-purple-50',
    sms: 'border-l-green-400 bg-green-50',
    tracker_com: 'border-l-amber-400 bg-amber-50',
    status_change: 'border-l-gray-400 bg-gray-50',
    invoice_emitted: 'border-l-indigo-400 bg-indigo-50',
    payment_received: 'border-l-teal-400 bg-teal-50',
    voicemail: 'border-l-rose-400 bg-rose-50'
  }
  const color = colors[t.sub_type] || 'border-l-gray-300 bg-white'
  return (
    <div className={`border-l-4 ${color} px-3 py-2 rounded`}>
      <div className="flex items-baseline gap-2">
        <span className="text-xs font-semibold uppercase text-gray-700">{t.sub_type}</span>
        <span className="text-xs text-gray-500">{t.sender_role}</span>
        {t.author_name && <span className="text-xs text-gray-600">· {t.author_name}</span>}
        {t.email_has_attachments && <span className="text-xs">📎</span>}
        <span className="text-xs text-gray-500 ml-auto">{new Date(t.created_at).toLocaleString('fr-BE')}</span>
      </div>
      {t.email_subject && (
        <div className="text-sm font-semibold mt-0.5">{t.email_subject}</div>
      )}
      {t.content_preview && (
        <div className="text-xs text-gray-700 mt-1 whitespace-pre-wrap line-clamp-3">
          {t.content_preview}
        </div>
      )}
    </div>
  )
}
